/**
 * Coverage Tracker Component
 * Dashboard view showing overall compliance status and stats
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  AlertCircle, 
  CheckCircle2, 
  Upload, 
  FileText, 
  Calendar,
  ChevronRight
} from 'lucide-react';

export default function CoverageTracker({ framework, requirements, coverage }) {
  // Calculate stats
  const stats = calculateStats(requirements, coverage);

  // Get requirements needing attention (mandatory + incomplete)
  const needsAttention = requirements
    .filter(req => {
      const reqCoverage = coverage?.requirementsCoverage?.[req.id];
      return req.isMandatory && (!reqCoverage || reqCoverage.status !== 'complete');
    })
    .slice(0, 10);

  return (
    <div className="space-y-6" data-testid="coverage-tracker">
      {/* Overall Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <StatCard
          icon={TrendingUp}
          iconColor="text-emerald-400"
          iconBg="bg-emerald-500/20"
          title="Overall Progress"
          value={`${stats.percentage}%`}
          subtitle={
            <div className="w-full h-2 bg-[#1A1A1A] rounded-full overflow-hidden mt-2">
              <div 
                className="h-full bg-emerald-500 rounded-full"
                style={{ width: `${stats.percentage}%` }}
              />
            </div>
          }
        />

        <StatCard
          icon={CheckCircle2}
          iconColor="text-emerald-400"
          iconBg="bg-emerald-500/20"
          title="Complete"
          value={stats.complete}
          subtitle={`of ${stats.total} requirements`}
        />

        <StatCard
          icon={FileText}
          iconColor="text-teal-400"
          iconBg="bg-teal-500/20"
          title="Evidence Uploaded"
          value={stats.totalEvidence}
          subtitle="files across all requirements"
        />

        <StatCard
          icon={Calendar}
          iconColor="text-blue-400"
          iconBg="bg-blue-500/20"
          title="Claims Created"
          value={stats.totalClaims}
          subtitle="ready for disclosure"
        />
      </div>

      {/* Status Breakdown */}
      <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Coverage Breakdown</h3>
        
        <div className="space-y-3">
          {/* Complete */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle2 size={24} className="text-emerald-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Complete</p>
                <p className="text-xs text-[#A0A0A0]">Evidence uploaded + claims approved</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-emerald-400">{stats.complete}</p>
              <p className="text-xs text-[#6B6B6B]">
                {Math.round((stats.complete / stats.total) * 100)}%
              </p>
            </div>
          </div>

          {/* Partial */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                <AlertCircle size={24} className="text-amber-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Partially Complete</p>
                <p className="text-xs text-[#A0A0A0]">Evidence or claims in progress</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-amber-400">{stats.partial}</p>
              <p className="text-xs text-[#6B6B6B]">
                {Math.round((stats.partial / stats.total) * 100)}%
              </p>
            </div>
          </div>

          {/* Missing */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A]">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#2A2A2A] flex items-center justify-center">
                <FileText size={24} className="text-[#6B6B6B]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Not Started</p>
                <p className="text-xs text-[#A0A0A0]">No evidence or claims yet</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#6B6B6B]">{stats.missing}</p>
              <p className="text-xs text-[#6B6B6B]">
                {Math.round((stats.missing / stats.total) * 100)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Requirements Needing Attention */}
      {needsAttention.length > 0 && (
        <div className="bg-[#0D0D0D] border border-amber-500/30 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-2">
            <AlertCircle size={20} className="text-amber-400" />
            Mandatory Requirements Needing Attention
          </h3>
          <p className="text-sm text-[#6B6B6B] mb-4">
            {needsAttention.length} mandatory requirement{needsAttention.length > 1 ? 's' : ''} incomplete
          </p>

          <div className="space-y-2">
            {needsAttention.map(req => {
              const reqCoverage = coverage?.requirementsCoverage?.[req.id];
              return (
                <Link
                  key={req.id}
                  to={`/frameworks/csrd-esrs?requirement=${req.id}`}
                  className="flex items-center justify-between p-3 rounded-lg border border-[#1A1A1A] hover:border-amber-500/30 hover:bg-amber-500/5 transition-colors group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 text-xs font-mono bg-[#1A1A1A] text-[#6B6B6B] rounded">
                        {req.requirementCode}
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        reqCoverage?.status === 'partial'
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'bg-[#1A1A1A] text-[#6B6B6B]'
                      }`}>
                        {reqCoverage?.status || 'missing'}
                      </span>
                    </div>
                    <p className="text-sm text-[#A0A0A0] truncate">{req.requirementTitle}</p>
                  </div>
                  <ChevronRight size={16} className="text-[#4A4A4A] group-hover:text-amber-400 transition-colors shrink-0 ml-4" />
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <Link
            to="/evidence/upload"
            className="flex items-center gap-3 p-4 rounded-lg border border-[#1A1A1A] hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-colors group"
          >
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <Upload size={18} className="text-emerald-400" />
            </div>
            <span className="text-sm font-medium text-[#A0A0A0] group-hover:text-white transition-colors">
              Upload Evidence
            </span>
          </Link>

          <Link
            to="/cycles/active"
            className="flex items-center gap-3 p-4 rounded-lg border border-[#1A1A1A] hover:border-blue-500/30 hover:bg-blue-500/5 transition-colors group"
          >
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Calendar size={18} className="text-blue-400" />
            </div>
            <span className="text-sm font-medium text-[#A0A0A0] group-hover:text-white transition-colors">
              View Disclosure Cycles
            </span>
          </Link>

          <Link
            to="/audit/trail"
            className="flex items-center gap-3 p-4 rounded-lg border border-[#1A1A1A] hover:border-purple-500/30 hover:bg-purple-500/5 transition-colors group"
          >
            <div className="p-2 rounded-lg bg-purple-500/20">
              <TrendingUp size={18} className="text-purple-400" />
            </div>
            <span className="text-sm font-medium text-[#A0A0A0] group-hover:text-white transition-colors">
              View Audit Trail
            </span>
          </Link>

          <Link
            to="/evidence/cross-framework"
            className="flex items-center gap-3 p-4 rounded-lg border border-[#1A1A1A] hover:border-teal-500/30 hover:bg-teal-500/5 transition-colors group"
          >
            <div className="p-2 rounded-lg bg-teal-500/20">
              <FileText size={18} className="text-teal-400" />
            </div>
            <span className="text-sm font-medium text-[#A0A0A0] group-hover:text-white transition-colors">
              Cross-Framework Map
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, iconColor, iconBg, title, value, subtitle }) {
  return (
    <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-5">
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-lg ${iconBg}`}>
          <Icon size={20} className={iconColor} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-[#6B6B6B] mb-1">{title}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
          {typeof subtitle === 'string' ? (
            <p className="text-xs text-[#6B6B6B] mt-1">{subtitle}</p>
          ) : subtitle}
        </div>
      </div>
    </div>
  );
}

// Helper function to calculate stats
function calculateStats(requirements, coverage) {
  let complete = 0;
  let partial = 0;
  let totalEvidence = 0;
  let totalClaims = 0;

  requirements.forEach(req => {
    const reqCoverage = coverage?.requirementsCoverage?.[req.id];
    if (reqCoverage?.status === 'complete') {
      complete++;
    } else if (reqCoverage?.status === 'partial') {
      partial++;
    }
    totalEvidence += reqCoverage?.evidenceCount || 0;
    totalClaims += reqCoverage?.claimsCount || 0;
  });

  const missing = requirements.length - complete - partial;
  const percentage = requirements.length > 0 
    ? Math.round((complete / requirements.length) * 100) 
    : 0;

  return {
    total: requirements.length,
    complete,
    partial,
    missing,
    percentage,
    totalEvidence: coverage?.totalEvidenceUploaded || totalEvidence,
    totalClaims: coverage?.totalClaimsCreated || totalClaims
  };
}
